// ─── OrderLaborDialog ────────────────────────────────────────────────
//
// Dialog de gestão de mão-de-obra do BM Petrotec (AO). Permite ao
// supervisor distribuir as horas reais entre os vários códigos
// contabilísticos (700100A09 mecânica, 700100A10 electricidade…).
//
// Modo de utilização:
//   · Botão "Auto-preencher" → cria UMA entry no código 700100A09
//     com os totais H.N./H.E. calculados pelo algoritmo (servidor).
//   · O supervisor pode depois apagar/editar/criar novas entries
//     para repartir entre códigos (ex.: 4h mecânica + 1h electricidade).
//
// Só admin/super_admin tem permissão para mexer (backend valida).

import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, IconButton, CircularProgress, TextField, MenuItem,
  Select, FormControl, InputLabel, Tooltip, Chip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import { AuthContext } from '../../App';
import ConfirmDialog from '../common/ConfirmDialog';

const fmtHours = (h) => Number(h || 0).toFixed(2);

export default function OrderLaborDialog({ open, onClose, orderId, orderRef, lang = 'pt' }) {
  const pt = lang === 'pt';
  const { token, userRole } = useContext(AuthContext);
  const headers = useMemo(() => ({
    Authorization: `Bearer ${token}`, 'Content-Type': 'application/json',
  }), [token]);

  // O AuthContext expõe `userRole` (não `user`) — usar o campo certo,
  // caso contrário isAdmin ficava sempre false e o popup aparecia
  // readonly mesmo para admins.
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [entries, setEntries] = useState([]);
  const [codes, setCodes] = useState([]);
  const [autoFilling, setAutoFilling] = useState(false);
  const [saving, setSaving] = useState(false);
  // Entry pendente de apagar — abre ConfirmDialog profissional em vez
  // do window.confirm() nativo.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Form para nova entry
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({ accountingCodeId: '', hoursHN: '', hoursHE: '', notes: '' });

  const fetchData = useCallback(async () => {
    if (!token || !orderId) return;
    setLoading(true);
    setError(null);
    try {
      const [entriesR, codesR] = await Promise.all([
        fetch(`/planner-api/order-labor/${orderId}`, { headers }),
        fetch('/planner-api/accounting-codes', { headers }),
      ]);
      if (!entriesR.ok) throw new Error(`HTTP ${entriesR.status}`);
      if (!codesR.ok) throw new Error(`HTTP ${codesR.status}`);
      const eData = await entriesR.json();
      const cData = await codesR.json();
      setEntries(Array.isArray(eData) ? eData : []);
      // Só códigos activos com preço H.N. definido
      const activeCodes = (Array.isArray(cData) ? cData : []).filter(c => c.isActive && c.priceHN != null);
      setCodes(activeCodes);
    } catch (e) {
      setError(pt ? 'Não foi possível carregar.' : 'Could not load.');
    } finally {
      setLoading(false);
    }
  }, [token, orderId, headers, pt]);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  // Limpar form ao fechar
  useEffect(() => { if (!open) { setShowForm(false); setDraft({ accountingCodeId: '', hoursHN: '', hoursHE: '', notes: '' }); } }, [open]);

  const totals = useMemo(() => {
    let hn = 0, he = 0, eur = 0;
    for (const e of entries) {
      hn += Number(e.hoursHN || 0);
      he += Number(e.hoursHE || 0);
      const pHN = Number(e.accountingCode?.priceHN || 0);
      const pHE = Number(e.accountingCode?.priceHE || 0);
      eur += Number(e.hoursHN || 0) * pHN + Number(e.hoursHE || 0) * pHE;
    }
    return { hn, he, eur };
  }, [entries]);

  const handleAutoFill = async () => {
    if (!isAdmin || autoFilling) return;
    setAutoFilling(true);
    setError(null);
    try {
      const r = await fetch(`/planner-api/order-labor/${orderId}/auto-fill`, {
        method: 'POST', headers,
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      const j = await r.json();
      setEntries(j.entries || []);
    } catch (e) {
      setError(e.message || (pt ? 'Erro ao auto-preencher.' : 'Auto-fill error.'));
    } finally {
      setAutoFilling(false);
    }
  };

  const handleCreate = async () => {
    if (!isAdmin || saving) return;
    if (!draft.accountingCodeId) { setError(pt ? 'Escolha um código.' : 'Pick a code.'); return; }
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`/planner-api/order-labor/${orderId}`, {
        method: 'POST', headers,
        body: JSON.stringify({
          accountingCodeId: parseInt(draft.accountingCodeId),
          hoursHN: Number(draft.hoursHN) || 0,
          hoursHE: Number(draft.hoursHE) || 0,
          notes: draft.notes || null,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      const created = await r.json();
      setEntries(prev => [...prev, created]);
      setShowForm(false);
      setDraft({ accountingCodeId: '', hoursHN: '', hoursHE: '', notes: '' });
    } catch (e) {
      setError(e.message || (pt ? 'Erro ao criar.' : 'Create error.'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id, patch) => {
    if (!isAdmin) return;
    try {
      const r = await fetch(`/planner-api/order-labor/entries/${id}`, {
        method: 'PUT', headers, body: JSON.stringify(patch),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      const updated = await r.json();
      setEntries(prev => prev.map(e => e.id === id ? updated : e));
    } catch (e) {
      setError(e.message);
    }
  };

  const performDelete = async () => {
    if (!isAdmin || !deleteTarget) return;
    setDeleting(true);
    try {
      const r = await fetch(`/planner-api/order-labor/entries/${deleteTarget.id}`, { method: 'DELETE', headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setEntries(prev => prev.filter(e => e.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: '1px solid #e2e8f0' }}>
        <Box>
          <Typography variant="h6" style={{ fontWeight: 700, fontSize: '1.05rem' }}>
            {pt ? 'Mão-de-obra (BM)' : 'Labor (BM)'}
          </Typography>
          {orderRef && (
            <Typography variant="caption" style={{ color: '#64748b' }}>
              {pt ? 'OT' : 'Order'} {orderRef}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: '16px 20px' }}>
        {loading ? (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {error && (
              <Box style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: '0.8rem' }}>
                {error}
              </Box>
            )}

            {/* Totais + Auto-fill */}
            <Box style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, padding: '10px 14px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
              <Box style={{ flex: 1, display: 'flex', gap: 16 }}>
                <Box>
                  <Typography variant="caption" style={{ color: '#64748b', fontSize: '0.7rem' }}>{pt ? 'Total H.N.' : 'Total H.N.'}</Typography>
                  <Typography style={{ fontWeight: 700, fontSize: '1rem', color: '#16a34a' }}>{fmtHours(totals.hn)} h</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" style={{ color: '#64748b', fontSize: '0.7rem' }}>{pt ? 'Total H.E.' : 'Total H.E.'}</Typography>
                  <Typography style={{ fontWeight: 700, fontSize: '1rem', color: '#d97706' }}>{fmtHours(totals.he)} h</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" style={{ color: '#64748b', fontSize: '0.7rem' }}>{pt ? 'Subtotal m.o.' : 'Subtotal labor'}</Typography>
                  <Typography style={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>{totals.eur.toFixed(2)} €</Typography>
                </Box>
              </Box>
              {isAdmin && (
                <Tooltip title={pt ? 'Calcula H.N./H.E. pelo horário do cliente final + feriados Angola' : 'Computes H.N./H.E. from final customer schedule + AO holidays'}>
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={autoFilling ? <CircularProgress size={14} /> : <FlashOnIcon />}
                      onClick={handleAutoFill}
                      disabled={autoFilling}
                      style={{ textTransform: 'none', borderColor: '#6366f1', color: '#4338ca', fontWeight: 600 }}
                    >
                      {pt ? 'Auto-preencher' : 'Auto-fill'}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>

            {/* Lista de entries */}
            {entries.length === 0 ? (
              <Box style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem', border: '1px dashed #cbd5e1', borderRadius: 10 }}>
                {pt ? 'Sem horas registadas. Use Auto-preencher ou Adicionar.' : 'No hours recorded. Use Auto-fill or Add.'}
              </Box>
            ) : (
              <Box style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                <Box style={{ display: 'grid', gridTemplateColumns: '2fr 90px 90px 90px 1.5fr 36px', gap: 8, padding: '8px 12px', background: '#f1f5f9', fontSize: '0.7rem', color: '#475569', fontWeight: 600 }}>
                  <span>{pt ? 'Código' : 'Code'}</span>
                  <span style={{ textAlign: 'right' }}>H.N.</span>
                  <span style={{ textAlign: 'right' }}>H.E.</span>
                  <span style={{ textAlign: 'right' }}>€</span>
                  <span>{pt ? 'Notas' : 'Notes'}</span>
                  <span></span>
                </Box>
                {entries.map((e) => {
                  const pHN = Number(e.accountingCode?.priceHN || 0);
                  const pHE = Number(e.accountingCode?.priceHE || 0);
                  const subtotal = Number(e.hoursHN || 0) * pHN + Number(e.hoursHE || 0) * pHE;
                  return (
                    <Box key={e.id} style={{ display: 'grid', gridTemplateColumns: '2fr 90px 90px 90px 1.5fr 36px', gap: 8, padding: '8px 12px', borderTop: '1px solid #f1f5f9', alignItems: 'center', fontSize: '0.78rem' }}>
                      <Box>
                        <Typography style={{ fontWeight: 600, fontSize: '0.78rem' }}>{e.accountingCode?.code || '—'}</Typography>
                        <Typography style={{ color: '#64748b', fontSize: '0.7rem' }}>{e.accountingCode?.description || ''}</Typography>
                      </Box>
                      <TextField
                        type="number"
                        size="small"
                        value={e.hoursHN}
                        disabled={!isAdmin}
                        inputProps={{ step: 0.25, min: 0, style: { textAlign: 'right', fontSize: '0.78rem', padding: '4px 6px' } }}
                        onChange={(ev) => setEntries(prev => prev.map(x => x.id === e.id ? { ...x, hoursHN: ev.target.value } : x))}
                        onBlur={(ev) => {
                          const v = Number(ev.target.value) || 0;
                          if (v !== Number(e.hoursHN)) handleUpdate(e.id, { hoursHN: v });
                        }}
                      />
                      <TextField
                        type="number"
                        size="small"
                        value={e.hoursHE}
                        disabled={!isAdmin}
                        inputProps={{ step: 0.25, min: 0, style: { textAlign: 'right', fontSize: '0.78rem', padding: '4px 6px' } }}
                        onChange={(ev) => setEntries(prev => prev.map(x => x.id === e.id ? { ...x, hoursHE: ev.target.value } : x))}
                        onBlur={(ev) => {
                          const v = Number(ev.target.value) || 0;
                          if (v !== Number(e.hoursHE)) handleUpdate(e.id, { hoursHE: v });
                        }}
                      />
                      <Typography style={{ textAlign: 'right', fontWeight: 600 }}>{subtotal.toFixed(2)}</Typography>
                      <TextField
                        size="small"
                        value={e.notes || ''}
                        disabled={!isAdmin}
                        placeholder="—"
                        inputProps={{ style: { fontSize: '0.75rem', padding: '4px 6px' } }}
                        onChange={(ev) => setEntries(prev => prev.map(x => x.id === e.id ? { ...x, notes: ev.target.value } : x))}
                        onBlur={(ev) => {
                          if ((ev.target.value || '') !== (e.notes || '')) handleUpdate(e.id, { notes: ev.target.value });
                        }}
                      />
                      {isAdmin && (
                        <IconButton size="small" onClick={() => setDeleteTarget(e)} style={{ padding: 4 }}>
                          <DeleteIcon style={{ fontSize: 16, color: '#dc2626' }} />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Form de nova entrada */}
            {isAdmin && (
              <Box style={{ marginTop: 12 }}>
                {showForm ? (
                  <Box style={{ padding: 12, border: '1px solid #c7d2fe', background: '#eef2ff', borderRadius: 10 }}>
                    <Box style={{ display: 'grid', gridTemplateColumns: '2fr 100px 100px 1.5fr', gap: 8, marginBottom: 8 }}>
                      <FormControl variant="outlined" size="small">
                        <InputLabel>{pt ? 'Código' : 'Code'}</InputLabel>
                        <Select
                          value={draft.accountingCodeId}
                          onChange={(ev) => setDraft({ ...draft, accountingCodeId: ev.target.value })}
                          label={pt ? 'Código' : 'Code'}
                        >
                          {codes.map(c => (
                            <MenuItem key={c.id} value={c.id} style={{ fontSize: '0.78rem' }}>
                              {c.code} — {c.description}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField label="H.N." type="number" size="small" variant="outlined" inputProps={{ step: 0.25, min: 0 }}
                        value={draft.hoursHN} onChange={(ev) => setDraft({ ...draft, hoursHN: ev.target.value })} />
                      <TextField label="H.E." type="number" size="small" variant="outlined" inputProps={{ step: 0.25, min: 0 }}
                        value={draft.hoursHE} onChange={(ev) => setDraft({ ...draft, hoursHE: ev.target.value })} />
                      <TextField label={pt ? 'Notas' : 'Notes'} size="small" variant="outlined"
                        value={draft.notes} onChange={(ev) => setDraft({ ...draft, notes: ev.target.value })} />
                    </Box>
                    <Box style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <Button size="small" onClick={() => { setShowForm(false); setDraft({ accountingCodeId: '', hoursHN: '', hoursHE: '', notes: '' }); }}>
                        {pt ? 'Cancelar' : 'Cancel'}
                      </Button>
                      <Button size="small" variant="contained" color="primary" disabled={saving} onClick={handleCreate}>
                        {saving ? <CircularProgress size={14} /> : (pt ? 'Adicionar' : 'Add')}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setShowForm(true)} style={{ textTransform: 'none', color: '#4338ca', fontWeight: 600 }}>
                    {pt ? 'Adicionar código' : 'Add code'}
                  </Button>
                )}
              </Box>
            )}

            {!isAdmin && (
              <Box style={{ marginTop: 12, padding: 8, background: '#fef3c7', borderRadius: 8, fontSize: '0.75rem', color: '#92400e', textAlign: 'center' }}>
                {pt ? 'Apenas administradores podem editar.' : 'Only admins can edit.'}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose}>{pt ? 'Fechar' : 'Close'}</Button>
      </DialogActions>

      {/* Confirmação profissional para apagar entries — alinha com o
          ConfirmDialog usado no resto da app em vez do window.confirm. */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => { if (!deleting) setDeleteTarget(null); }}
        onConfirm={performDelete}
        severity="warning"
        title={pt ? 'Apagar entrada de mão-de-obra' : 'Delete labor entry'}
        confirmLabel={deleting
          ? (pt ? 'A apagar…' : 'Deleting…')
          : (pt ? 'Apagar' : 'Delete')}
        cancelLabel={pt ? 'Cancelar' : 'Cancel'}
        message={(() => {
          if (!deleteTarget) return '';
          const code = deleteTarget.accountingCode?.code || '—';
          const desc = deleteTarget.accountingCode?.description || '';
          const hn = Number(deleteTarget.hoursHN || 0).toFixed(2);
          const he = Number(deleteTarget.hoursHE || 0).toFixed(2);
          return pt
            ? `Vais apagar a entrada do código ${code}${desc ? ` (${desc})` : ''} — ${hn}h H.N. + ${he}h H.E.\n\nO custo correspondente desaparece desta OT. Esta acção não pode ser desfeita.`
            : `You are deleting entry for code ${code}${desc ? ` (${desc})` : ''} — ${hn}h H.N. + ${he}h H.E.\n\nThe corresponding cost is removed from this OT. This cannot be undone.`;
        })()}
      />
    </Dialog>
  );
}
